<template>
  <div class="scores-container-wrapper">
    <div class="scores-columns-wrapper">
      <div class="scores-column-charts px-1">
        <div class="d-flex flex-row justify-space-between mb-2">
          <div class="form-inline">
            <fai :icon="['far', 'chart-line']"/>
            <select
              class="form-control input"
              @input="
                event => {
                  scoreFilterHandler(event.target.value);
                }
              "
            >
              <option
                v-for="(option, index) in days"
                :key="index"
                :value="option.value"
              >
                <b>{{ option.name }}</b>
              </option>
            </select>
          </div>
          <section class="inline">
            <div class="scores-type-wrapper mt-2 pt-2">
              <a
                id="btn-positive" 
                class="positive"
                v-on:click="onClickFilter('Positive');"
                >
                <span v-if="Positive">
                  <fai class="color-green" :icon="['fas', 'plus-circle']" />
                </span>
                <span v-else>
                  <fai class="color-green" :icon="['fas', 'minus-circle']" />
                </span>
                positive
              </a>
              <Tooltip
                :text="btnTooltips('Positive')"
                :show-delay="500"
                target="#btn-positive"
                name="filterPositives"
                custom-class="score-filter-button-tooltip positive"
                append-to=".scores-type-wrapper"
              />

              <a
                id="btn-neutral"
                class="neutral"
                v-on:click="onClickFilter('Neutral');"
                >
                <span v-if="Neutral">
                  <fai class="color-yellow" :icon="['fas', 'plus-circle']" />
                </span>
                <span v-else>
                  <fai class="color-yellow" :icon="['fas', 'minus-circle']" />
                </span>
                neutral
              </a>
              <Tooltip
                :text="btnTooltips('Neutral')"
                :show-delay="500"
                target="#btn-neutral"
                name="filterNeutrals"
                custom-class="score-filter-button-tooltip neutral"
                append-to=".scores-type-wrapper"
              />

              <a
                id="btn-negative"
                class="negative"
                v-on:click="onClickFilter('Negative');"
                >
                <span v-if="Negative">
                  <fai class="color-red" :icon="['fas', 'plus-circle']" />
                </span>
                <span v-else>
                  <fai class="color-red" :icon="['fas', 'minus-circle']" />
                </span>
                negative
              </a>
              <Tooltip
                :text="btnTooltips('Negative')"
                :show-delay="500"
                target="#btn-negative"
                name="filterNegatives"
                custom-class="score-filter-button-tooltip negative"
                append-to=".scores-type-wrapper"
              />
            </div>
          </section>
        </div>
        <div class="d-flex justify-center" v-if="showLoading.graph">
          <sb-loading-icon />
        </div>
        <ScoresChart
            v-if="scores.length != 0 && !showLoading.graph"
            :active="false"
            chartType="nps"
            title="nps scores"
            :scores="scores.nps"
            :chartItemSelected="handleChartSelection"
          />

        <ScoresChart
            v-if="scores.length != 0 && !showLoading.graph"
            :active="false"
            chartType="crs"
            title="cs scores"
            :scores="scores.crs"
            :chartItemSelected="handleChartSelection"
          />

        <ScoresChart
            v-if="scores.length != 0 && !showLoading.graph"
            :active="false"
            chartType="product"
            title="product scores"
            :scores="scores.product"
            :chartItemSelected="handleChartSelection"
          />
      </div>

      <div class="scores-column-comments px-1">
        <div class="comments-title">
          <h4 class="text-left">{{ dataConfig.params.scoreType == 'crs' ? 'cs' : dataConfig.params.scoreType }} scores and comments</h4>
          <div class="d-flex justify-center" v-if="showLoading.comment">
            <sb-loading-icon />
          </div>
          <div v-else class="mb-2 timeline-wrapper" :style="`overflow-x: hidden; `">
            <div id="timelineTop" />
            <transition-group appear name="list" tag="ul" class="timeline">
              <li :key="`Not_found`" v-if="comments.length == 0 || comments.data.length == 0">
                <fai :icon="['far', 'comments-alt']"><span class="nps-score-in-icon">?</span></fai>
                <div class="timeline-item box box-brand">
                  <div class="timeline-body ">
                    <h4 class="my-0">
                      <fai :icon="['far', 'exclamation-triangle']" class="mr-1"/>
                      No feedback found...
                    </h4>
                  </div>
                </div>
              </li>

              <TimelineItem
                v-else
                v-for="comment in comments.data"
                :item="comment"
                :commentsType="dataConfig.params.scoreType"
                :key="comment.id"
              />
            </transition-group>
          </div>

          <div v-if="comments.length != 0 && !showLoading.comment" class="border-top text-center pt-2">
            <div class="row">
              <div class="col-xs-12">
                <div class="d-flex justify-space-between align-center">
                  <div class="d-flex justify-content-center align-center">
                    <button
                      class="btn prev-btn btn-primary btn-small"
                      :disabled="comments.current_page == 1"
                      :class="{'disabled': comments.current_page == 1}"
                      @click="handleGetPage('prev')"
                    >
                      <fai :icon="['far', 'chevron-left']" class="mr-1"/>prev
                    </button>
                  </div>
                  <div class="d-flex justify-content-center align-center">
                    showing {{ paginationText }} of {{comments.total}} results
                  </div>
                  <div class="d-flex justify-content-center align-center">
                    <button
                      class="btn next-btn btn-primary btn-small"
                      :disabled="comments.current_page == comments.last_page"
                      :class="{'disabled': comments.current_page == comments.last_page}"
                      @click="handleGetPage('next')"
                      >
                      next<fai :icon="['far', 'chevron-right']" class="ml-1"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { feedbackAPI } from "../../../../services/api/feedbackAPI"
import ScoresChart from '../../../Graphs/ScoresChart';
import TimelineItem from '../TimelineItem';
import { BtnGroup, Btn, Tooltip } from "uiv";
import moment from 'moment';

export default {
  name: "ScoresContainer",
  components: {
    ScoresChart,
    TimelineItem,
    Btn,
    BtnGroup,
    Tooltip
  },
  data() {
    return {
      comments: [],
      dataConfig: {
        params: {
          days: 7,
          specDay: null,
          scoreType: 'nps',
          scoreCompare: null,
          page: 1
        }
      },
      days: [
        {
          value: "7",
          name: "last 7 days"
        },
        {
          value: "14",
          name: "last 14 days"
        },
        {
          value: "30",
          name: "last 30 days"
        }
      ],
      scores: [],
      Positive: false,
      Neutral: false,
      Negative: false,
      showLoading: {
        graph: true,
        comment: true
      }
    };
  },
  async created() {
    this.scores = await feedbackAPI.GETScoresSummaryGraphs(this.dataConfig)
    this.comments = await feedbackAPI.GETScoresSummaryComments(this.dataConfig)
    this.$root.$on("setStore", () => {
      this.refresh()
    });
    this.showLoading.graph = false
    this.showLoading.comment = false
  },
  computed: {
    paginationText() {
      const from = this.comments.from
      const to = this.comments.to

      if(from && to) return `${from}-${to}`
      return 0
    }
  },
  methods: {
    async refresh() {
      this.showLoading.graph = true
      this.showLoading.comment = true
      this.scores = await feedbackAPI.GETScoresSummaryGraphs(this.dataConfig)
      this.comments = await feedbackAPI.GETScoresSummaryComments(this.dataConfig)

      //Reset graph filters
      this.Positive = false
      this.Neutral = false
      this.Negative = false

      this.showLoading.graph = false
      this.showLoading.comment = false
    },
    handleGetPage: async function (direction) {
      this.showLoading.comment = true

      const { comments } = this;

      if (direction == 'prev') {
        if (comments.current_page == 1) return;

        this.dataConfig.params.page = comments.current_page - 1;
        
      } else if (direction == 'next') {
        if (comments.current_page == comments.last_page) return;

        this.dataConfig.params.page = comments.current_page + 1;
      }

      window.scrollTo(0,0)

      this.comments = await feedbackAPI.GETScoresSummaryComments(this.dataConfig);
      this.showLoading.comment = false
    },
    async handleChartSelection(day, scoreType, chartType) {
      this.showLoading.comment = true
      const scoreTypeArr = ["<", "=", ">"]

      const isDeselect = 
        ( this.dataConfig.params.scoreCompare == scoreTypeArr[scoreType] ) &&
        ( this.dataConfig.params.scoreType == chartType ) &&
        ( this.dataConfig.params.specDay == day );

      this.dataConfig.params.specDay = isDeselect ? null : day;
      this.dataConfig.params.scoreType = isDeselect ? 'nps' : chartType;
      this.dataConfig.params.scoreCompare = isDeselect ? null : scoreTypeArr[scoreType]
      this.dataConfig.params.page = 1;

      this.comments = await feedbackAPI.GETScoresSummaryComments(this.dataConfig);
      this.showLoading.comment = false
    },
    scoreFilterHandler(intDays) {
      this.dataConfig.params.days = intDays;
      this.dataConfig.params.specDay = null;
      this.dataConfig.params.page = 1;

      this.refresh()
    },
    onClickFilter(seriesName) {      
      this.$root.$emit("toggleDataSeries", seriesName);
      this[seriesName] = !this[seriesName];
    },
    btnTooltips(seriesName) {
      let strTooltip = `${seriesName} scores`;
      if (this[seriesName]) strTooltip = `show ${strTooltip.toLowerCase()} `;
      if (!this[seriesName]) strTooltip = `hide ${strTooltip.toLowerCase()} `;
      return strTooltip;
    }
  }
};
</script>